import { useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'

export interface LineChartSeries {
  key: string | number
  label: string
  /** Fast hex-farve (matcher Avatar-paletten), ikke et Tailwind-token - identitet skal ikke skifte med tema. */
  color: string
  /** Kumuleret værdi pr. runde. Index 0 = runde 0 (start), længde = antal runder + 1. */
  points: number[]
}

interface LineChartProps {
  series: LineChartSeries[]
  ariaLabel: string
  height?: number
}

function fmt(n: number): string {
  return (n > 0 ? '+' : '') + n
}

/** "Pæne" tal til gitterlinjer, altid inkl. 0. */
function niceTicks(min: number, max: number): number[] {
  const range = Math.max(max - min, 1)
  const rawStep = range / 4
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)))
  const norm = rawStep / mag
  const step = (norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10) * mag
  const ticks: number[] = []
  let t = Math.floor(min / step) * step
  for (; t <= max + step * 0.001; t += step) ticks.push(Math.round(t))
  return ticks
}

const W = 560
const H_DEFAULT = 220
const L = 46
const R = 12
const T = 14

/** Pointkurve: kumulerede point pr. spiller over runderne, med hover-crosshair. */
function LineChart({ series, ariaLabel, height = H_DEFAULT }: LineChartProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)

  const n = Math.max(0, ...series.map((s) => s.points.length - 1))
  const allValues = series.flatMap((s) => s.points)
  const maxV = Math.max(10, ...allValues, 0)
  const minV = Math.min(-10, ...allValues, 0)
  const B = 26
  const plotH = height - T - B

  const x = (i: number) => L + (n <= 0 ? 0 : (i / n) * (W - L - R))
  const y = (v: number) => T + ((maxV - v) / (maxV - minV || 1)) * plotH

  const ticks = niceTicks(minV, maxV)

  const endpoints = series
    .map((s) => ({ ...s, v: s.points[s.points.length - 1] ?? 0 }))
    .sort((a, b) => y(a.v) - y(b.v))
  let lastLabelY = -Infinity
  const labelPositions = new Map<string | number, number>()
  endpoints.forEach((e) => {
    let ly = y(e.v)
    if (ly - lastLabelY < 11) ly = lastLabelY + 11
    labelPositions.set(e.key, ly)
    lastLabelY = ly
  })

  const updateHover = (clientX: number) => {
    const svg = svgRef.current
    if (!svg || n <= 0) return
    const rect = svg.getBoundingClientRect()
    const relX = ((clientX - rect.left) / rect.width) * W
    const raw = ((relX - L) / (W - L - R)) * n
    setHoverIndex(Math.max(0, Math.min(n, Math.round(raw))))
  }

  const handlePointerMove = (e: ReactPointerEvent<SVGSVGElement>) => updateHover(e.clientX)
  const handlePointerLeave = () => setHoverIndex(null)

  return (
    <div className="grid gap-2">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${height}`}
        role="img"
        aria-label={ariaLabel}
        className="block h-auto w-full touch-none select-none"
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
        onPointerDown={handlePointerMove}
      >
        <g className="font-mono text-[10px]">
          {ticks.map((v) => (
            <g key={v}>
              <line
                x1={L}
                x2={W - R}
                y1={y(v)}
                y2={y(v)}
                className={v === 0 ? 'stroke-text-2/50' : 'stroke-text-2/15'}
                strokeWidth={1}
              />
              <text x={L - 6} y={y(v) + 3} textAnchor="end" className="fill-text-2">
                {v}
              </text>
            </g>
          ))}
          {n > 0 &&
            Array.from({ length: n }, (_, i) => i + 1).map((i) => (
              <text key={i} x={x(i)} y={height - 6} textAnchor="middle" className="fill-text-2">
                {i}
              </text>
            ))}
        </g>

        {hoverIndex !== null && (
          <line
            x1={x(hoverIndex)}
            x2={x(hoverIndex)}
            y1={T}
            y2={T + plotH}
            className="stroke-text-2/40"
            strokeWidth={1}
            strokeDasharray="3,3"
          />
        )}

        {series.map((s) => (
          <polyline
            key={s.key}
            stroke={s.color}
            strokeWidth={2.5}
            fill="none"
            strokeLinejoin="round"
            strokeLinecap="round"
            points={s.points.map((v, i) => `${x(i)},${y(v)}`).join(' ')}
          />
        ))}

        {hoverIndex !== null &&
          series.map((s) => (
            <circle key={s.key} cx={x(hoverIndex)} cy={y(s.points[hoverIndex] ?? 0)} r={3.5} fill={s.color} className="stroke-felt" strokeWidth={1.5} />
          ))}

        {endpoints.map((e) => (
          <g key={e.key}>
            <circle cx={x(n)} cy={y(e.v)} r={3.5} fill={e.color} className="stroke-felt" strokeWidth={2} />
            <text x={x(n) - 6} y={(labelPositions.get(e.key) ?? y(e.v)) + 3} textAnchor="end" className="font-mono text-[10px] font-semibold" fill={e.color}>
              {fmt(e.v)}
            </text>
          </g>
        ))}
      </svg>

      {hoverIndex !== null && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 rounded-lg bg-ink/25 px-2.5 py-1.5 text-[0.72rem]">
          <span className="font-mono font-semibold text-text-2">#{hoverIndex}</span>
          {series.map((s) => (
            <span key={s.key} className="flex items-center gap-1">
              <i aria-hidden style={{ background: s.color }} className="inline-block h-2.5 w-2.5 rounded-full" />
              <span className="text-text-2">{s.label}</span>
              <b className="font-mono">{fmt(s.points[hoverIndex] ?? 0)}</b>
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2.5 text-[0.78rem]">
        {series.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5">
            <i aria-hidden style={{ background: s.color }} className="inline-block h-[3px] w-2.5 rounded-sm" />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  )
}

export default LineChart
